import PropTypes from "prop-types";
import ContextedReduceStore from "./util/ContextedReduceStore";
import NavigationActions from "./NavigationActions";
import typeCheckingStore from "./util/typeCheckingStore";
import { recordIdType } from "./util/typedefs";
import Dispatcher from "Dispatcher";
import RecordActions from "./RecordActions";
import { isConfirmed } from "./OverStore";
import SuperRecordActions from "data/SuperRecordActions";
import EntityName from "data/util/EntityName";
import dotProp from "dot-prop-immutable";
import StereotypesStore from "data/StereotypesStore";
import TableStore from "data/TableStore";
import { isVersionInfluencing } from "data/selectors/entityFields";
import MapButtonActions from "data/MapButtonActions";
import MergeActions from "./MergeActions";
import MappingActions from "./MappingActions";
import MergeStore, { MERGE_PLUGINS } from "./MergeStore";
import SuperRecordStore from "./SuperRecordStore";
import ClientId from "data/ClientId";

export const isRecordSelected = (state, entityKey, id) => {
    const rec = state.record;
    if (rec == null) return false;
    return rec.key === entityKey && rec.id === id;
};

export const isVersionSelected = (state, entityOrVersionKey) => {
    const magic = state.magic;
    if (magic == null) return false;
    if (magic.versionKey == null) return false;
    return magic.versionKey === new EntityName(entityOrVersionKey).versionKey;
};

export const isImplementationSelected = (state, implementationKey) => {
    const magic = state.magic;
    if (magic == null) return false;
    if (magic.versionKey != null) return false;
    return magic.implementationKey === implementationKey;
};

export const isImplementationExpanded = (state, implementationKey) => {
    const magic = state.magic;
    if (magic == null) return false;
    if (magic.expandedImplKeys != null) {
        if (magic.expandedImplKeys.indexOf(implementationKey) >= 0) {
            return true;
        }
    }
    if (magic.versionKey == null) return false;
    return new EntityName(magic.versionKey).implKey === implementationKey;
};

export const isAllImplementationsSelected = state => {
    const magic = state.magic;
    return magic.implementationKey == null && magic.versionKey == null;
};

const addDistinct = (items, newItem) => {
    if (items == null) return [newItem];
    if (items.indexOf(newItem) >= 0) return items;
    return items.concat(newItem);
};

const removeDistinct = (items, oldItem) => {
    if (items == null) return null;
    const idx = items.indexOf(oldItem);
    if (idx < 0) return items;
    if (idx === 0) items.slice(1);
    items = items.slice();
    items.splice(idx, 1);
    return items;
};

const _deleteRecord = (context, action) => {
    if (context.record == null || context.record.id !== action.recordId) return context;
    const en = new EntityName(action.recordKey);
    return {
        magic: {
            ...context.magic,
            implementationKey: en.implKey,
            versionKey: en.versionKey,
        },
        record: {
            // this is gonna cause problems for non-magics (globals)
            key: StereotypesStore.getStereotypeByKey(context.magic.key)
                .implementations.find(it =>
                    it.childKey === action.recordKey)
                .key,
            id: context.magic.id,
        },
    };
};

const _deleteChild = (context, action) => {
    if (context.record == null || context.record.id !== action.childId) return context;
    // select the parent record
    return {
        ...context,
        record: {
            key: action.recordKey,
            id: action.recordId,
        },
    };
};

const _merge_delete = (context) => {
    if(MergeStore.isReady()) return context; // not merging anything
    Dispatcher.waitFor([ // todo: get this.dispatcher...
        TableStore.getDispatchToken(),
        SuperRecordStore.getDispatchToken(),
    ]);
    switch(MergeStore.getPluginKey()) {
        case MERGE_PLUGINS.RECORD_MERGE: {
            const action = MergeStore.getDeleteAction();
            return _deleteRecord(context, action);
        }

        case MERGE_PLUGINS.CHILD_MERGE: {
            const action = MergeStore.getDeleteAction();
            return _deleteChild(context, action);
        }
        // nothing to delete on the client
        default:
            return context;
    }
};

function openRecord(context, key, id) {
    if (isRecordSelected(
        context,
        key,
        id,
    )) {
        return context;
    }
    const next = {
        ...context,
        record: {
            key: key,
            id: id,
        },
    };
    if (context.magic && context.magic.id === id) {
        const en = new EntityName(key);
        next.magic = {
            ...next.magic,
            implementationKey: en.implKey,
            expandedImplKeys: addDistinct(
                next.magic.expandedImplKeys,
                en.implKey,
            ),
            versionKey: en.versionKey,
        };
    }
    return next;
}

const processSROpenState = (context, action) => {
    if (isRecordSelected(
        context,
        action.entityKey,
        action.recordId,
    )) {
        return {
            ...context, // this allows the target record to be set before the superrecord is loaded
            magic: {
                key: action.key,
                id: action.id,
            },
        };
    }
    const next = {
        ...context,
        magic: {
            key: action.key,
            id: action.id,
        },
        record: action.entityKey != null && action.recordId != null ? {
            key: action.entityKey,
            id: action.recordId,
        } : null,
    };
    if (context.magic && context.magic.id === action.recordId) {
        const en = new EntityName(action.entityKey);
        next.magic = {
            ...next.magic,
            implementationKey: en.implKey,
            expandedImplKeys: addDistinct(
                next.magic.expandedImplKeys,
                en.implKey,
            ),
            versionKey: en.versionKey,
        };
    }
    return next;
};

const processSelectImplementationState = (context, action) => {
    if (isImplementationSelected(
        context,
        action.implementationKey,
    )) {
        return context;
    }
    const magic = {
        ...context.magic,
        implementationKey: action.implementationKey,
        expandedImplKeys: addDistinct(
            context.magic.expandedImplKeys,
            action.implementationKey,
        ),
    };
    delete magic.versionKey;
    return {
        magic,
        // kill record
    };
};

const processToggleImplementationState = (context, action) => {
    const open = isImplementationExpanded(
        context,
        action.implementationKey,
    );
    const magic = {
        ...context.magic,
    };
    magic.expandedImplKeys = open
        ? removeDistinct(
            magic.expandedImplKeys,
            action.implementationKey,
        )
        : addDistinct(
            magic.expandedImplKeys,
            action.implementationKey,
        );
    if (open && magic.versionKey != null) {
        const vk = new EntityName(magic.versionKey);
        if (vk.implKey === action.implementationKey) {
            magic.versionKey = null;
        }
    }
    return {
        ...context,
        magic,
    };
};

const processSelectAllImplementationsState = (context) => {
    const magic = {
        ...context.magic,
    };
    delete magic.versionKey;
    delete magic.implementationKey;
    return {
        magic,
        // kill record
    };
};

const processNewRecordState = (context, action) => {
    if (! action.navigate) return context;
    const en = new EntityName(action.recordKey);
    return {
        magic: {
            key: action.stereoKey,
            id: action.newId,
            implementationKey: en.implKey,
            expandedImplKeys: [en.implKey],
            versionKey: en.versionKey,
        },
        record: {
            key: action.recordKey,
            id: action.newId,
        },
    };
};

const processNewMagicChildState = (context, action) => {
    if (! action.navigate) return context;
    const en = new EntityName(action.entityKey);
    const magic = {
        ...context.magic,
        expandedImplKeys: addDistinct(
            context.magic.expandedImplKeys,
            en.implKey,
        ),
    };
    if (isAllImplementationsSelected(context)) {
        // sweet!
    } else if (isImplementationSelected(context, en.implKey)) {
        // also good enough!
    } else if (isVersionSelected(context, action.entityKey)) {
        // and good enough again!
    } else {
        magic.implementationKey = en.implKey;
        magic.versionKey = null;
    }
    return {
        magic,
        record: {
            key: action.entityKey,
            id: action.newId,
        },
    };
};

const processSetFieldState = (context, action) => {
    // and that there WAS a change
    if (!TableStore.hasChanged()) return context;
    const ep = TableStore.getEntityProxy(
        action.recordKey,
        action.recordId,
    );
    if (!isVersionInfluencing(action.recordKey, action.fieldKey)) {
        return context;
    }
    if (ep.isCorrectVersion()) return context;
    // so it changed versions. figure out what the new one is...
    const tp = ep.tableProxy;
    const eks = tp.getEntityKeys();
    if (eks.length === 0) {
        throw new Error(`Record '${ep.getKey()}' has '${action.fieldKey}': '${action.value}'. This matches no version.`);
    }
    if (eks.length > 1) {
        throw new Error(`Record '${ep.getKey()}' has '${action.fieldKey}': '${action.value}'. This matches multiple versions (${eks.join(", ")}).`);
    }
    context = {
        ...context,
        record: {
            ...context.record,
            key: eks[0],
        },
    };
    if (context.magic != null && new EntityName(action.recordKey).versionKey === context.magic.versionKey) {
        context.magic = {
            ...context.magic,
            versionKey: new EntityName(eks[0]).versionKey,
        };
    }
    return context;
};

const processDoMergeState = (context, action) => {
    if(!isConfirmed(action)) return context;
    switch(MergeStore.getPluginKey()) {
        case MERGE_PLUGINS.RECORD_MERGE:
            return _deleteRecord(context, action);
        case MERGE_PLUGINS.CHILD_MERGE:
            return _deleteChild(context, action);
        default:
            return context;
    }
};

const processLoadedState = (context, action) => {
    const magic = context.magic;
    if (magic == null) return context;
    if (magic.key !== action.key) return context;
    if (magic.id !== action.id) return context;
    const rec = context.record;
    if (rec == null) return context;
    if (!ClientId.is(rec.id)) return context;
    //  reloading the same magic with the current record being
    // client-only means the current record is no more.
    return dotProp.delete(context, "record");
};

const processUpdatedState = (context, action) => {
    if (context.magic && action.data.newIds.hasOwnProperty(context.magic.id)) {
        context = dotProp.set(
            context,
            ["magic", "id"],
            action.data.newIds[context.magic.id],
        );
    }
    if (context.record && action.data.newIds.hasOwnProperty(context.record.id)) {
        context = dotProp.set(
            context,
            ["record", "id"],
            action.data.newIds[context.record.id],
        );
    }
    return context;
};

class NavigatorStore extends ContextedReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialContext() {
        return {};
    }

    reduceContext(context, action) {
        switch (action.type) {

            case SuperRecordActions.OPEN: {
                return processSROpenState(context, action);
            }

            case NavigationActions.SELECT_RECORD:
            case NavigationActions.FOCUS_FIELD: {
                return openRecord(context, action.entityKey, action.recordId);
            }

            case NavigationActions.SELECT_IMPLEMENTATION: {
                return processSelectImplementationState(context, action);
            }

            case NavigationActions.TOGGLE_IMPLEMENTATION: {
                return processToggleImplementationState(context, action);
            }

            case NavigationActions.SELECT_ALL_IMPLEMENTATIONS: {
                return processSelectAllImplementationsState(context, action);
            }

            case SuperRecordActions.NEW: {
                return processNewRecordState(context, action);
            }

            case SuperRecordActions.NEW_MAGIC_CHILD:
            case MapButtonActions.AUTOMAP_TO_NEW_MAGIC_CHILD: {
                return processNewMagicChildState(context, action);
            }

            case RecordActions.NEW_CHILD: {
                if (!action.navigate) {
                    return context;
                }
                return openRecord(context, action.entityKey, action.newId);
            }

            default:
                return context;
        }
    }

    reduceEachContext(context, action) {
        switch (action.type) {

            case RecordActions.SET_FIELD: {
                // ensure the change is recorded
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                ]);
                return processSetFieldState(context, action);
            }

            case MappingActions.IT_IS_DONE: {
                // This is not needed for the current merge flow (Merge From...).
                // But if we ever decide to go the other direction (Merge To...)
                // this will be necessary.
                return _merge_delete(context);
            }

            case MergeActions.DO_MERGE: {
                // wait for merge to be processed then delete.
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                    MergeStore.getDispatchToken(),
                ]);

                return processDoMergeState(context, action);
            }

            case RecordActions.DELETE_RECORD: {
                if (! isConfirmed(action)) return context;
                return _deleteRecord(context, action);
            }

            case RecordActions.DELETE_CHILD:
                if (! isConfirmed(action)) return context;
                return _deleteChild(context, action);

            case SuperRecordActions.LOADED: {
                return processLoadedState(context, action);
            }

            case SuperRecordActions.CREATED:
            case SuperRecordActions.UPDATED: {
                return processUpdatedState(context, action);
            }

            case SuperRecordActions.DELETED:
                if (context.magic == null || context.magic.id !== action.id) return context;
                return this.getInitialContext();

            default:
                return context;
        }
    }

    getMagicState(context) {
        return this.getState(context).magic;
    }

    getRecordState(context) {
        return this.getState(context).record;
    }

}

NavigatorStore.contextTypes = {
    magic: PropTypes.exact({
        key: PropTypes.string,
        id: recordIdType,
        implementationKey: PropTypes.string,
        expandedImplKeys: PropTypes.arrayOf(PropTypes.string),
        versionKey: PropTypes.string,
    }),
    record: PropTypes.shape({
        key: PropTypes.string,
        id: recordIdType,
    }),
};

export default typeCheckingStore(new NavigatorStore());



// WEBPACK FOOTER //
// ./app/data/NavigatorStore.js